import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  BillingDetails,
  loadStripe,
  PaymentIntentResult,
  PaymentMethod,
  Stripe,
  StripeCardNumberElement,
  StripeElement,
} from '@stripe/stripe-js';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { FormFieldName } from 'src/app/shared/models/application/formFieldName';
import {
  CheckoutModel,
  Option,
  PaymentResult,
} from 'src/app/shared/models/application/paymentOptions';
import {
  AddressHelper,
  AddressHelperFactory,
} from 'src/shared-app-portal/services/address-helper.service';
import { ApplicationService } from 'src/app/shared/services/application.service';
import { SnackBarService } from 'src/app/shared/services/snackbar.service';
import { IPaymentPreview } from 'src/app/shared/models/application/applicationSubmit';

class ForceNoError implements ErrorStateMatcher {
  isErrorState(): boolean {
    return false;
  }
}
class ForceError implements ErrorStateMatcher {
  isErrorState(): boolean {
    return true;
  }
}
const fieldsRequiredForBillingAddress: Partial<Record<FormFieldName, boolean>> = {
  Country: true,
  Address1: true,
  City: true,
  StateOrProvince: true,
  PostalCode: true,
};
const hiddenFieldsForBillingAddress: Partial<Record<FormFieldName, boolean>> = {
  AddressIsShared: true,
};

@Component({
  selector: 'application-payment',
  templateUrl: './application-payment.component.html',
  styleUrls: ['./application-payment.component.scss'],
})
export class ApplicationPaymentComponent implements OnInit, OnDestroy {
  form: FormGroup<{
    nameOnCard: FormControl<string>;
    hiddenField: FormControl<string>;
    nameOnAccount: FormControl<string>;
    isUsingAddressProvided: FormControl<boolean>;
  }>;
  @Input()
  inPreviewMode: boolean = false;
  @Input()
  previewFormId: string;
  //for using same address
  addressFromApplication: DemographicInfo;

  manualMailingAddress: AddressHelper;

  constructor(
    formBuilder: FormBuilder,
    private applicationService: ApplicationService,
    private snackBarService: SnackBarService,
    private addressHelperFactory: AddressHelperFactory
  ) {
    this.form = formBuilder.group({
      nameOnCard: ['', [Validators.required]],
      hiddenField: '',
      nameOnAccount: '',
      isUsingAddressProvided: [false],
    });
  }
  async ngOnInit() {
    this.manualMailingAddress = await this.addressHelperFactory.buildAddressHelper(
      null,
      '',
      fieldsRequiredForBillingAddress,
      hiddenFieldsForBillingAddress
    );
  }

  ngOnDestroy(): void {
    this.itemsToDestroy.forEach((element) => {
      element.destroy();
    });
    this.itemsToDestroy = [];
  }
  isFetchingData: boolean = true;
  paymentOptions: CheckoutModel;
  selectedPaymentOption: Option;
  stripe: Stripe;
  cardNumber: StripeCardNumberElement;
  achPaymentMethod: PaymentMethod;
  cardNumberError: ErrorStateMatcher = new ForceNoError();
  cvcError: ErrorStateMatcher = new ForceNoError();
  expiryError: ErrorStateMatcher = new ForceNoError();
  itemsToDestroy: StripeElement[] = [];
  displayedColumns: Array<string> = ['label', 'amount'];
  errorMessage = '';

  /** called each time stepper opens payment page */
  async loadPayments(
    secureLinkId: string,
    addressFromApplication: DemographicInfo,
    paymentPreview: IPaymentPreview
  ) {
    this.form.controls.isUsingAddressProvided.setValue(false); //reset checkbox each time incase they went back in make the billing address invalid
    this.addressFromApplication = addressFromApplication?.isUsableForBillingAddress()
      ? addressFromApplication
      : null;
    this.isFetchingData = true;
    try {
      if (this.inPreviewMode) {
        this.paymentOptions = await this.applicationService.getCheckoutOptionsForPreview(
          this.previewFormId,
          paymentPreview
        );
      } else {
        this.paymentOptions = await this.applicationService.getCheckoutOptions(
          secureLinkId,
          paymentPreview
        );
      }
    } catch (error) {
      if (error.error) {
        if (error.error === 'Stripe Publishable key not set') {
          this.errorMessage = `Stripe Publishable key not set. Please contact Lineup Support at support@lineupteams.com and we'll help you out.`;
          return;
        }
        this.snackBarService.open(error.error, false);
      } else {
        this.snackBarService.open('Error occurred. Please try again.', false);
      }
      console.error(error);
      this.errorMessage = 'Error occurred. Please try again.';
      return;
    }
    this.isFetchingData = false;
    this.selectedPaymentOption = this.paymentOptions.options[0];
    if (!this.paymentOptions.isCheckoutAvailable) {
      //checkout not available. No need to init stripe.
      //probably $0 checkout
      return;
    }

    const singlePaymentOption =
      this.paymentOptions.options?.length === 1 ? this.paymentOptions.options[0] : null;

    if (singlePaymentOption?.paymentType === 'Check') {
      //only checkout option is check. No need to init stripe.
      return;
    }

    if (!this.paymentOptions.publicKey) {
      //need stripe public key for CC and/or ACH
      this.showStripeSnackError();
      return;
    }
    this.stripe = await loadStripe(this.paymentOptions.publicKey);

    const elements = this.stripe.elements();

    const cardExpiry = elements.create('cardExpiry');
    cardExpiry.mount('#card-expiry');
    this.itemsToDestroy.push(cardExpiry);

    this.cardNumber = elements.create('cardNumber');
    this.cardNumber.mount('#card-number');
    this.itemsToDestroy.push(this.cardNumber);

    const cardCvc = elements.create('cardCvc');
    cardCvc.mount('#card-cvc');

    this.itemsToDestroy.push(cardCvc);
  }

  //entry point from ApplicationComponent
  //returns false (and display snack error) if there is problem
  //returns a string of the paymentIntentId if successful
  //paymentIntentId will be 'check' if payment method is check
  completePayment = async (): Promise<PaymentResult> => {
    this.clearManualStripeErrors();
    if (this.paymentOptions?.isCheckoutAvailable === false) {
      return PaymentResult.None;
    }
    const paymentType = this.selectedPaymentOption?.paymentType;
    try {
      if (paymentType === 'Card') {
        return new PaymentResult(paymentType, await this.submitCreditCard());
      }
      if (paymentType === 'Check') {
        return new PaymentResult(paymentType);
      }
      if (paymentType === 'Ach') {
        //defaultError = 'Error accepting ACH';
        return new PaymentResult(paymentType, await this.acceptMandate());
      }
      if (paymentType) {
        this.snackBarService.open(`Unhandled paymentMethod: ${paymentType}`, false);
      } else {
        this.snackBarService.open(`You must pick a payment method`, false);
      }
      return new PaymentResult(paymentType, false);
    } catch (error) {
      this.snackBarService.open(error.message ? error.message : error, false);
      console.error(error);
      return new PaymentResult(paymentType, false);
    }
  };

  private getBillingAddress = (): DemographicInfo => {
    if (this.form.value.isUsingAddressProvided && this.addressFromApplication) {
      return this.addressFromApplication;
    }
    return this.manualMailingAddress.valid
      ? this.manualMailingAddress.getDemographicInfoFromForm()
      : null;
  };

  private getBillingDetails = (): BillingDetails => {
    const name = this.form.controls.nameOnCard.value;
    if (!name) {
      this.form.controls.nameOnCard.markAsTouched(); //no need to display error with snack bar because field turns red
    }

    const address = this.getBillingAddress();
    if (!address) {
      this.manualMailingAddress.addressFormGroup.markAllAsTouched();
      return;
    }

    if (!name) {
      return;
    }

    if (!address.country?.code2) {
      this.snackBarService.open('Could not find country code!', false); //stripe will fail is code2 is not set
      console.error(`could not find country code. ${JSON.stringify(address.country)}`);
      return;
    }

    return {
      name: name,
      address: {
        line1: address.streetAddress1,
        line2: address.streetAddress2,
        city: address.city,
        state: address.region,
        postal_code: address.postalCode,
        country: address.country.code2,
      },
    };
  };

  private getStripeAndSnackIfProblem = (): {
    stripe?: Stripe;
    clientSecret?: string;
  } => {
    if (this.stripe && this.selectedPaymentOption.clientSecret) {
      //everything is okay
      return {
        stripe: this.stripe,
        clientSecret: this.selectedPaymentOption.clientSecret,
      };
    }
    this.showStripeSnackError();
    return {};
  };

  private showStripeSnackError = () => {
    const message = this.inPreviewMode
      ? 'Cannot submit ACH and credit card payments when in preview mode.'
      : 'Could not initialize payment. Cannot submit.';
    this.snackBarService.open(message, false);
  };

  private submitCreditCard = async (): Promise<false | string> => {
    const billing_details = this.getBillingDetails();
    if (!billing_details) {
      return false;
    }
    const { stripe, clientSecret } = this.getStripeAndSnackIfProblem();
    if (!stripe) {
      return false;
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: this.cardNumber,
        billing_details,
      },
    });
    if (error) {
      if (!this.manuallyForceErrorState(error.code)) {
        //if the error state didn't update, show the snack message
        this.snackBarService.open(error.message, false);
      }
      return false;
    }

    if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
      //happy path
      return paymentIntent.id;
    }

    this.snackBarService.open(`Unknown status ${paymentIntent.status}`, false);
    return false;
  };

  private async connectBankToLineup(nameOnAccount: string): Promise<PaymentIntentResult | false> {
    try {
      const { stripe, clientSecret } = this.getStripeAndSnackIfProblem();
      if (!stripe) {
        return false;
      }

      //opens stripe modal
      return await stripe.collectBankAccountForPayment({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: { billing_details: { name: nameOnAccount } },
        },
        expand: ['payment_method'],
      });
    } catch (exception) {
      return { error: exception }; //move exception onto result.error
    }
  }

  private getNameOnAccount = (): string => {
    const nameOnAccount = this.form.controls.nameOnAccount.value;
    if (nameOnAccount) {
      return this.form.controls.nameOnAccount.value;
    }
    this.form.controls.nameOnAccount.markAsTouched();
    this.snackBarService.open('Enter the name of the person on your bank account.', false);
    return '';
  };

  async connectACH() {
    const nameOnAccount = this.getNameOnAccount();
    if (!nameOnAccount) {
      return; //snack error handled in getNameOnAccount
    }
    const result = await this.connectBankToLineup(nameOnAccount);
    if (result === false) {
      return;
    }
    const { error, paymentIntent } = result;

    if (error) {
      console.error(error.message || error);
      this.snackBarService.open(error.message || error.toString(), false);
      return;
    }
    const status = paymentIntent?.status;

    if (status === 'requires_confirmation') {
      // We collected an account - possibly instantly verified, but possibly
      // manually-entered. Display payment method details and mandate text
      // to the customer and confirm the intent once they accept
      // the mandate.
      [];
      if (!paymentIntent.payment_method) {
        console.error(paymentIntent);
        this.snackBarService.open('Connect ACH failed. No payment method found', false);
        return;
      }
      this.achPaymentMethod = paymentIntent.payment_method as PaymentMethod;
      this.form.controls.nameOnAccount.disable(); //AFAIK, the only way to update the email and name is to remove and re-connect the payment method
      return; //success
    }

    if (status === 'requires_payment_method') {
      this.snackBarService.open('ACH payment flow cancelled', false);
    } else {
      this.snackBarService.open(`Connect ACH failed. Unknown status:${status}`, false);
      console.error(paymentIntent);
    }
  }

  removeACH() {
    this.form.controls.nameOnAccount.enable();
    this.achPaymentMethod = null;
  }

  private async acceptMandate(): Promise<false | string> {
    const nameOnAccount = this.getNameOnAccount();
    if (!nameOnAccount) {
      return false; //snack error handled in getNameOnAccount
    }
    if (!this.achPaymentMethod?.id) {
      this.snackBarService.open('Need to search for your bank', false);
      return false;
    }

    const { stripe, clientSecret } = this.getStripeAndSnackIfProblem();
    if (!stripe) {
      return false;
    }

    const { paymentIntent, error } = await stripe.confirmUsBankAccountPayment(clientSecret, {
      payment_method: this.achPaymentMethod.id,
    });

    if (error) {
      // The payment failed for some reason.
      console.error(error.message || error);
      this.snackBarService.open(error.message || error.toString(), false);
      return false;
    }

    if (paymentIntent.status === 'requires_payment_method') {
      // Confirmation failed. Attempt again with a different payment method.
      this.snackBarService.open('Payment requires payment method', false);

      return false;
    }
    if (paymentIntent.status === 'processing') {
      //success!
      return paymentIntent.id;
    }
    if (paymentIntent.next_action?.type === 'verify_with_microdeposits') {
      //2 success paths

      // The account needs to be verified via microdeposits.
      // Display a message to consumer with next steps (consumer waits for
      // microdeposits, then enters an amount on a page sent to them via email).
      //const hostedUrl =paymentIntent.next_action.verify_with_microdeposits?.hosted_verification_url;
      //this.notificationService.notifySuccess(`<a href="${hostedUrl}">verify micro deposits</a>`);
      //success!
      return paymentIntent.id;
    }
    //unknown
    console.error(paymentIntent);
    const errorMessage = `Unknown status from Stripe: ${paymentIntent.status} ${paymentIntent.next_action?.type}`;
    console.error(errorMessage);
    this.snackBarService.open(errorMessage, false);

    return false;
  }

  private clearManualStripeErrors() {
    this.cardNumberError = new ForceNoError();
    this.cvcError = new ForceNoError();
    this.expiryError = new ForceNoError();
    //clear other fields too?
  }

  private manuallyForceErrorState(code: string): boolean {
    if (!code) {
      return false;
    }
    if (/_number/.test(code)) {
      this.cardNumberError = new ForceError();
      return true;
    }
    if (/_cvc/.test(code)) {
      this.cvcError = new ForceError();
      return true;
    }
    if (/expiry/.test(code)) {
      this.expiryError = new ForceError();
      return true;
    }
    return false;
  }
}
